import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { CreditCard, DollarSign, Receipt, ReceiptText, TrendingUp } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import DataExportButton from "@/components/shared/DataExportButton";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { SearchInput } from "@/components/shared/SearchInput";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuthStore } from "@/store/authStore";
import { useAdminPayments } from "@/hooks/useAdminData";
import { demoPayments } from "@/data/demoData";

export const Route = createFileRoute("/admin/payments/")({
  component: AdminPaymentsPage,
});

const formatTZS = (amount: number) =>
  new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
  }).format(amount);

function AdminPaymentsPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { user } = useAuthStore();
  const { data: paymentsData = [], isLoading } = useAdminPayments();

  const displayPayments = user?.isDemo ? demoPayments : paymentsData;

  const filteredPayments = displayPayments.filter((p: any) => {
    const student = p.student || p.studentName || "Unknown";
    const matchesSearch = student.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalRevenue = displayPayments
    .filter((p: any) => p.status === "completed")
    .reduce((sum: number, p: any) => sum + (p.amount || 0), 0);
  const pendingAmount = displayPayments
    .filter((p: any) => p.status === "pending")
    .reduce((sum: number, p: any) => sum + (p.amount || 0), 0);

  return (
    <div className="space-y-6 pb-4">
      <PageHeader
        title="Revenue & collections"
        subtitle="Manage platform fees, monitor transaction status, and track system-wide revenue health."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Payments" }]}
        action={
          <DataExportButton
            onExport={(format) => console.log("Exporting payments as", format)}
            label="Download financial report"
          />
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard
          label="Total platform revenue"
          value={totalRevenue}
          icon={<DollarSign />}
          trend={user?.isDemo ? { value: "22%", positive: true } : undefined}
        />
        <StatCard label="Pending collections" value={pendingAmount} icon={<ReceiptText />} />
        <StatCard
          label="Transaction volume"
          value={user?.isDemo && displayPayments.length === 0 ? "1,240" : displayPayments.length}
          icon={<TrendingUp />}
        />
      </div>

      <SectionCard
        title="Transaction ledger"
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by student…"
              className="min-w-0 flex-1 sm:w-60 sm:flex-none"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-[140px]" aria-label="Status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        noPadding
      >
        {isLoading ? (
          <TableSkeleton rows={5} columns={4} />
        ) : filteredPayments.length === 0 ? (
          <EmptyState
            bare
            icon={<ReceiptText />}
            title="No transactions"
            description="No transactions found for this period."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">Payer / student</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Payment channel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Process date</TableHead>
                <TableHead className="pr-4 text-right md:pr-5">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.map((payment: any) => (
                <TableRow key={payment.id} className="h-11 [&>td]:py-1.5">
                  <TableCell className="min-w-[160px] pl-4 font-medium text-foreground md:pl-5">
                    {payment.student || payment.studentName || "Unknown"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right font-mono text-foreground tabular-nums">
                    {formatTZS(payment.amount)}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-body">
                      <CreditCard className="size-3.5 text-muted-foreground" aria-hidden />
                      {payment.method || payment.channel || "N/A"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={payment.status === "completed" ? "success" : "warning"}
                      dot
                      className="capitalize"
                    >
                      {payment.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {payment.date ||
                      (payment.createdAt
                        ? new Date(payment.createdAt).toLocaleDateString()
                        : "N/A")}
                  </TableCell>
                  <TableCell className="pr-4 text-right md:pr-5">
                    <IconAction label="Receipt" icon={<Receipt />} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
